import React, {useState} from "react";
import {useParams} from "react-router-dom";
import {DimensionPanel} from "../components/DimensionPanel";


import {StepDefinition, StepId, STEPS} from "../models/steps";
import {StepState, Turn} from "../models/assessment";
import {useAssessmentState} from "../hooks/useAssessmentState";




export default function AssessmentInterview() {
    const {id} = useParams<{ id: string }>();
    const [activeIndex, setActiveIndex] = useState<number>(0); // 0 = pre-interview

    const { state, loading, error, appendTurn } = useAssessmentState(id!);



    if (loading) {
        return <div style={{ padding: "1.5rem" }}>Loading assessment…</div>;
    }

    if (error) {
        return <div style={{ padding: "1.5rem", color: "red" }}>
            Error loading assessment: {error}
        </div>;
    }

    // If you want to guard against a totally missing state:
    if (!state) {
        return <div style={{ padding: "1.5rem", color: "red" }}>
            No assessment state returned from server.
        </div>;
    }

    const activeStepDef = STEPS[activeIndex];
    const activeStepState = state[activeStepDef.id];


    return (
        <div style={{fontFamily: "system-ui", padding: "1.5rem"}}>
            <h1 style={{marginTop: 0}}>Assessment interview</h1>

            <p style={{color: "#64748b", marginTop: 0}}>
                Assessment ID: <strong>{id}</strong>
            </p>

            {/* Top step bar */}
            <div
                style={{
                    display: "flex",
                    gap: "0.4rem",
                    margin: "1rem 0 1.5rem",
                    overflowX: "auto",
                }}
            >
                {STEPS.map((stepDef, index) => {
                    const isActive = index === activeIndex;
                    const stepState = state[stepDef.id]; // now safe, state is non-null

                    return (
                        <button
                            key={stepDef.id}
                            type="button"
                            title={stepDef.name}
                            onClick={() => setActiveIndex(index)}
                            style={getStepStyle(stepDef, stepState, isActive)}
                        >
                            {stepDef.id === "pre" ? "Pre" : stepDef.label}
                        </button>
                    );
                })}

            </div>

            {/* Main content area */}
            <div
                style={{
                    borderRadius: "0.75rem",
                    border: "1px solid #e2e8f0",
                    padding: "1.25rem 1.5rem",
                    width: "100%",
                    height: "70vh",
                    background: "#ffffff",
                    boxShadow: "0 10px 25px rgba(15, 23, 42, 0.08)",
                    boxSizing: "border-box",
                }}
            >
                <DimensionPanel
                    key={activeStepDef.id} // remount when switching dimensions
                    stepDef={activeStepDef}
                    stepState={activeStepState}          // ✅ this is a StepState
                    mode={activeStepDef.id === "pre" ? "pre" : "dimension"}
                    onAppendTurn={(turn) => appendTurn(activeStepDef.id, turn)}
                />

            </div>


            {/* Optional: next/previous controls */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "1rem",
                    maxWidth: "900px",
                }}
            >
                <button
                    type="button"
                    disabled={activeIndex === 0}
                    onClick={() => setActiveIndex((i) => Math.max(0, i - 1))}
                >
                    ← Previous
                </button>
                <button
                    type="button"
                    disabled={activeIndex === STEPS.length - 1}
                    onClick={() =>
                        setActiveIndex((i) =>
                            Math.min(STEPS.length - 1, i + 1)
                        )
                    }
                >
                    Next →
                </button>
            </div>
        </div>
    );
}



export function getStepStyle(
    step: StepDefinition,
    state: StepState,
    isActive: boolean
): React.CSSProperties {

    // Base style
    let style: React.CSSProperties = {
        minWidth: "2.2rem",
        padding: "0.35rem 0.6rem",
        borderRadius: "999px",
        fontSize: "0.85rem",
        fontWeight: 500,
        cursor: "pointer",
        whiteSpace: "nowrap",
        border: "1px solid #cbd5e1",
        background: "#ffffff",
        color: "#0f172a",
        transition: "box-shadow 0.12s ease, transform 0.12s ease",
    };

    //
    // 🎨 State-based styles (dynamic)
    //

    // ⚪ Not planned → soft, optional looking
    if (!state.planned) {
        style.border = "1px dashed #e2e8f0";
        style.color = "#94a3b8";
        style.background = "#f8fafc";
    }

    // 🟡 Planned but not completed → “to do”
    if (state.planned && !state.completed) {
        style.border = "1px solid #f59e0b";
        style.color = "#92400e";
        style.background = "#fffbeb";
    }

    // ✅ Completed → green pill
    if (state.completed) {
        style.border = "1px solid #16a34a";
        style.background = "#16a34a";
        style.color = "#ffffff";
        style.fontWeight = 600;
    }

    //
    // 🔵 Active (selected step)
    //
    if (isActive) {
        style.boxShadow = "0 0 0 2px rgba(15,23,42,0.25)";
        style.transform = "translateY(-1px)";
        style.borderWidth = "2px"; // keep the same color, just emphasize
    }

    return style;
}
